/**
 * DropOverlay Component - Merch/Music drops + Support Artist tips during stream
 */

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShoppingBag, Music, Heart, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TIP_PRESETS = [5, 10, 20, 50];

const DropOverlay = ({ visible, onClose, artistName, artistId }) => {
  const { token } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [buyingId, setBuyingId] = useState(null);
  const [showTip, setShowTip] = useState(false);
  const [tipAmount, setTipAmount] = useState(10);
  const [tipMessage, setTipMessage] = useState('');
  const [tipLoading, setTipLoading] = useState(false);
  const [error, setError] = useState('');

  const loadProducts = useCallback(async () => {
    if (!artistId) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/store/products?artist_id=${artistId}&limit=20`);
      const data = await res.json();
      setProducts(data.products || []);
    } catch (e) {
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [artistId]);

  useEffect(() => {
    if (visible) {
      setError('');
      setShowTip(false);
      loadProducts();
    }
  }, [visible, loadProducts]);

  const requireAuth = () => {
    if (!token) {
      setError('Please sign in to purchase or tip.');
      return false;
    }
    return true;
  };

  const handleBuy = async (product) => {
    if (!requireAuth()) return;
    setBuyingId(product.id);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/store/buy-now`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ origin_url: window.location.origin, product_id: product.id, quantity: 1 })
      });
      const data = await res.json();
      if (res.ok && data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        setError(data.detail || 'Could not start checkout.');
        setBuyingId(null);
      }
    } catch (e) {
      setError('Network error. Please try again.');
      setBuyingId(null);
    }
  };

  const handleTip = async () => {
    if (!requireAuth()) return;
    if (!artistId) { setError('Artist not available for tips.'); return; }
    setTipLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/store/tip`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ origin_url: window.location.origin, artist_id: artistId, amount: Number(tipAmount), message: tipMessage })
      });
      const data = await res.json();
      if (res.ok && data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        setError(data.detail || 'Could not start tip checkout.');
        setTipLoading(false);
      }
    } catch (e) {
      setError('Network error. Please try again.');
      setTipLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {visible && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            data-testid="drop-overlay-backdrop"
          />
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-[#0B0A10] border-l border-white/10 z-50 overflow-y-auto"
            data-testid="drop-overlay-panel"
          >
            <div className="sticky top-0 bg-[#0B0A10]/90 backdrop-blur-xl border-b border-white/10 p-4 flex items-center justify-between z-10">
              <div>
                <h2 className="text-xl font-bold text-white tracking-tight" style={{ fontFamily: 'Unbounded' }}>New Drops</h2>
                <p className="text-[#A1A1AA] text-sm mt-0.5">From {artistName || 'Artist'}</p>
              </div>
              <button onClick={onClose} className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center" data-testid="drop-overlay-close">
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            {error && (
              <div className="mx-4 mt-4 p-3 rounded-xl bg-red-500/15 border border-red-500/30 text-red-300 text-sm" data-testid="drop-overlay-error">
                {error}
              </div>
            )}

            <div className="p-4 space-y-4">
              {loading ? (
                <div className="flex items-center justify-center py-12 text-[#A1A1AA]">
                  <Loader2 className="w-6 h-6 animate-spin" />
                </div>
              ) : products.length === 0 ? (
                <div className="text-center py-12 text-[#71717A]" data-testid="drop-overlay-empty">
                  <ShoppingBag className="w-12 h-12 mx-auto mb-3 opacity-40" />
                  <p className="text-sm">No drops available yet.</p>
                  <p className="text-xs mt-1">You can still support the artist below.</p>
                </div>
              ) : (
                products.map((product, index) => (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.08 }}
                    className="group relative bg-[#12101A] rounded-2xl overflow-hidden border border-white/5 hover:border-[#00F0FF]/30 transition-colors"
                    data-testid={`drop-item-${product.id}`}
                  >
                    <div className="flex gap-4 p-4">
                      <div className="w-24 h-24 rounded-xl overflow-hidden flex-shrink-0 bg-[#1a1625]">
                        {product.images?.[0] ? (
                          <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            {product.category === 'music' ? <Music className="w-8 h-8 text-[#00F0FF]" /> : <ShoppingBag className="w-8 h-8 text-[#39FF14]" />}
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          {product.category === 'music' ? <Music className="w-4 h-4 text-[#00F0FF]" /> : <ShoppingBag className="w-4 h-4 text-[#39FF14]" />}
                          <span className={`text-xs uppercase tracking-wider ${product.category === 'music' ? 'text-[#00F0FF]' : 'text-[#39FF14]'}`}>{product.category}</span>
                        </div>
                        <h3 className="text-white font-semibold text-lg truncate">{product.name}</h3>
                        <p className="text-[#A1A1AA] text-sm truncate">{product.description}</p>
                        <div className="flex items-center justify-between mt-3">
                          <span className="text-white font-bold">${Number(product.price).toFixed(2)}</span>
                          <button
                            onClick={() => handleBuy(product)}
                            disabled={buyingId === product.id || product.stock <= 0}
                            className={`px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2 transition-all disabled:opacity-50 ${
                              product.category === 'music'
                                ? 'bg-[#00F0FF] text-[#050505] hover:bg-[#00F0FF]/80'
                                : 'bg-[#39FF14] text-[#050505] hover:bg-[#39FF14]/80'
                            }`}
                            data-testid={`buy-btn-${product.id}`}
                          >
                            {buyingId === product.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : product.stock <= 0 ? 'Sold Out' : (product.category === 'music' ? 'Buy' : 'Shop')}
                          </button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
            </div>

            {/* Support Artist */}
            <div className="p-4 mt-2 border-t border-white/10">
              {!showTip ? (
                <button
                  onClick={() => setShowTip(true)}
                  className="w-full py-4 rounded-2xl bg-gradient-to-r from-[#FF007F] to-[#FF007F]/80 text-white font-bold text-lg shadow-[0_0_30px_rgba(255,0,127,0.3)] hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                  data-testid="support-artist-btn"
                >
                  <Heart className="w-5 h-5" />
                  Support {artistName || 'Artist'}
                </button>
              ) : (
                <div className="space-y-3" data-testid="tip-panel">
                  <p className="text-white font-semibold">Send a tip to {artistName || 'Artist'}</p>
                  <div className="grid grid-cols-4 gap-2">
                    {TIP_PRESETS.map((amt) => (
                      <button
                        key={amt}
                        onClick={() => setTipAmount(amt)}
                        className={`py-2 rounded-xl font-semibold text-sm transition-colors ${
                          Number(tipAmount) === amt ? 'bg-[#FF007F] text-white' : 'bg-white/5 text-[#A1A1AA] hover:bg-white/10'
                        }`}
                        data-testid={`tip-preset-${amt}`}
                      >
                        ${amt}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 bg-white/5 rounded-xl px-3">
                    <span className="text-[#A1A1AA]">$</span>
                    <input
                      type="number"
                      min="1"
                      max="500"
                      value={tipAmount}
                      onChange={(e) => setTipAmount(e.target.value)}
                      className="flex-1 bg-transparent py-3 text-white outline-none"
                      data-testid="tip-custom-amount"
                    />
                  </div>
                  <input
                    type="text"
                    placeholder="Add a message (optional)"
                    value={tipMessage}
                    onChange={(e) => setTipMessage(e.target.value)}
                    maxLength={140}
                    className="w-full bg-white/5 rounded-xl px-3 py-3 text-white outline-none placeholder-[#52525B] text-sm"
                    data-testid="tip-message"
                  />
                  <button
                    onClick={handleTip}
                    disabled={tipLoading || Number(tipAmount) < 1}
                    className="w-full py-4 rounded-2xl bg-gradient-to-r from-[#FF007F] to-[#FF007F]/80 text-white font-bold text-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="tip-confirm-btn"
                  >
                    {tipLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Heart className="w-5 h-5" /> Tip ${Number(tipAmount || 0).toFixed(2)}</>}
                  </button>
                </div>
              )}
              <p className="text-center text-[#52525B] text-xs mt-2">Secure checkout powered by Stripe</p>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default DropOverlay;
